import catalogApi from '@/api/services/catalog'
import Util from '@/util'
import Store from '@/store'

const defaultState = () => {
  return {
    items: {},
    soldOut: {},
    categories: {},
    catalog: {},
    optionSets: {},
    channelvos: {},
    tags: {},
    discounts: {},
    charges: {},
    taxTypes: {},
    taxArea: {},
    brands: {},
    categoryGroups: {},
    itemSchedules: {},
    catalogTags: {},
    catalogCategories: [],
    filterCats: [],
    dataLoaded: false
  }
}

const state = defaultState()

const getters = {
  getCategoryById: (state) => (id) => {
    if (!state.categories) {
      return null;
    }
    return state.categories[id]
  },

  getCategoryName: (state, getters) => (id) => {
    const cat = getters.getCategoryById(id)

    if (cat) {
      return cat.name
    }
    return ''
  },

  getRecommended: (state, getters) => {
    if (!state.catalogTags) {
      return []
    }
    const recommended = []
    const recommendedTag = state.catalogTags.find(tag => tag.name === 'Chef Recommended')
    const isRecommended = (item) => !Util.isEmpty(item.info.tagIds) && item.info.tagIds.includes(recommendedTag.mid)

    if (!recommendedTag) {
      return recommended
    }

    for (let itemId in state.items) {
      const item = state.items[itemId]

      if ((item.type === 'Group' && item.variants.some(varItem => isRecommended(varItem))) ||
          isRecommended(item)) {
        recommended.push(getters.prepareItemVO(item))
      }
    }

    return recommended
  },

  getItem: (state) => (itemId) => {
    if (!state.items) {
      return null;
    }
    return state.items[itemId]
  },

  getOptionSet: (state) => (optionSetId) => {
    if (!state.optionSets) {
      return null;
    }
    return state.optionSets[optionSetId]
  },

  getChannel: (state) => (channelDefId) => {
    if (!state.channelvos) {
      return null;
    }
    return state.channelvos[channelDefId]
  },

  getTag: (state) => (tagId) => {
    if (!state.tags) {
      return null;
    }
    return state.tags[tagId]
  },

  getApplicableDeals: (state) => {
    const RL = window.RL
    if (!state.discounts) {
      return null;
    }
    try {
      return RL.dealutil.getApplicableDealsForMarket(Object.values(state.discounts))
    } catch (error) {
    }
  },

  isOutOfStock: (state) => (itemId) => {
    if (!state.soldOut) {
      return false;
    }
    return !!state.soldOut[itemId]
  },

  getEntityById: (state) => (entityName, id) => {
    if (!state[entityName]) {
      return null;
    }
    return state[entityName][id]
  },

  isDataLoaded: (state) => {
    return state.dataLoaded
  },

  isItemAvailable: (state, getters) => (item) => {
    const RL = window.RL
    const isAvailable = (item) => {
      let isAvailable = false
      try {
        isAvailable = RL.catalogutil.isItemAvailable({
          item: item,
          branchTime: Util.formatDate({ zoneId: Store.getters['business/timeZone'].zoneId }),
          channelDefId: Store.getters['business/channelDefId'],
          itemSchedules: Util.isEmpty(state.itemSchedules) ? null : Object.values(state.itemSchedules)
        })
      } catch (error) {
      }

      return isAvailable && !isNaN(getters.itemPrice(item))
    }

    if (Util.isEmpty(state.itemSchedules)) {
      return true
    }
    if (item.variants && !Util.isEmpty(item.variants)) {
      return item.variants.every(item => isAvailable(item))
    }
    return isAvailable(item)
  },

  itemPrice: (state, getters) => (item) => {
    const RL = window.RL
    let price = null

    try {
      price = RL.catalogutil.getItemPrice({
        item: item,
        branchTime: Util.formatDate({ zoneId: Store.getters['business/timeZone'].zoneId }),
        channelDefId: Store.getters['business/channelDefId'],
        groupId: getters.groupId(Store.getters['business/channelDefId']),
        itemSchedules: Util.isEmpty(state.itemSchedules) ? null : Object.values(state.itemSchedules)
      })
    } catch (error) {
    }

    return price
  },

  prepareItemVO: (state, getters) => (item) => {
    const getMinPrice = (item) => {
      let priceArr = [];

      if (item.type !== 'Group') {
        return getters.itemPrice(item);
      }
      item.variants.forEach(itemVar => {
        let p = getters.itemPrice(itemVar)
        if (Util.isValidPrice(p)) {
          priceArr.push(p)
        }
      })

      if (Util.isEmpty(priceArr)) {
        return null;
      }
      return Math.min(...priceArr);
    }

    const getVariantsTags = (variants) => {
      if (Util.isEmpty(variants)) {
        return null
      }
      var tagIds = []

      variants.forEach((variant) => {
        if (!Util.isEmpty(variant.info.tagIds)) {
          tagIds = tagIds.concat(variant.info.tagIds)
        }
      })

      return tagIds
    }

    const isSoldOut = (item) => {
      const checkSoldOut = ({ mid }) => {
        return !Util.isEmpty(state.soldOut) && !!state.soldOut[mid]
      }
      if (!Util.isEmpty(item.variants)) {
        return item.variants.every(item => checkSoldOut(item))
      }
      return checkSoldOut(item)
    }

    return {
      name: item.info.longName,
      imageURL: item.info.imageURL,
      imagePath: item.info.imagePath,
      mid: item.mid,
      description: item.info.description,
      categoryId: item.info.categoryId,
      hasItemVariants: !Util.isEmpty(item.variants),
      hasItemOptions: !Util.isEmpty(item.priceInfo.optionSetIds),
      price: getMinPrice(item),
      displayOrder: isSoldOut(item) || Util.isEmpty(item.displayOrder) ? 999 : item.displayOrder,
      outOfStock: isSoldOut(item),
      tagIds: item.type === 'Group' ? getVariantsTags(item.variants) : item.info.tagIds
    }
  },

  groupId: (state) => (channelDefId) => {
    if (Util.isEmpty(state.channelvos)) {
      return null
    }
    return state.channelvos[channelDefId].groupId
  }
}

const actions = {
  getCatalog ({ commit, getters, state, rootState }) {
    const params = {
      outletId: rootState.business.outletId
    }

    const updateCatalog = catalogData => {
      const catalogTags = () => {
        const pluckTags = (itemArr) => {
          return Util.removeArrFalsyVal(Util.unique(Util.pluck(itemArr, ['info', 'tagIds']).flat()))
        }
        let tagIds = pluckTags(catalogData.items)
        const variantItems = Util.removeArrFalsyVal(Util.pluck(catalogData.items, ['variants']).flat())
        let variantTagIds = pluckTags(variantItems)

        tagIds = Util.unique(tagIds.concat(variantTagIds))
        if (Util.isEmpty(tagIds)) {
          return null
        }
        return tagIds.map(tagId => {
          return getters.getEntityById('tags', tagId)
        })
      }

      const catalogCategories = () => {
        return Util.removeArrFalsyVal(Util.unique(Util.pluck(catalogData.items, ['info', 'categoryId']).flat()))
      }

      commit('setEntityCol', {
        name: 'items',
        data: catalogData.items,
        id: 'mid'
      })

      commit('setEntityCol', {
        name: 'channelvos',
        data: catalogData.channelvos,
        id: 'channelDefId'
      })

      const categoryMap = catalogData.categories.reduce((catMap, cat) => {
        catMap[cat.mid] = {
          mid: cat.mid,
          name: cat.name,
          displayOrder: !Util.isEmpty(cat.displayOrder) ? cat.displayOrder : 9999
        }

        if (cat.subCategories) {
          catMap[cat.mid].subCategories = Util.arrayToMap(cat.subCategories, 'mid')
        }
        return catMap
      }, {})

      commit('setEntityCol', {
        name: 'categories',
        entity: categoryMap
      })
      commit('setEntityCol', {
        name: 'catalogCategories',
        entity: catalogCategories()
      })
      commit('setEntityCol', {
        name: 'itemSchedules',
        data: catalogData.itemSchedules,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'tags',
        data: catalogData.itemTags,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'catalogTags',
        entity: catalogTags()
      })
      commit('setEntityCol', {
        name: 'discounts',
        data: catalogData.discounts,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'charges',
        data: catalogData.charges,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'taxTypes',
        data: catalogData.taxTypes,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'brands',
        data: catalogData.brands,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'categoryGroups',
        data: catalogData.categoryGroups,
        id: 'mid'
      })
      commit('setEntityCol', {
        name: 'taxArea',
        entity: catalogData.taxArea
      })

      const optionSets = catalogData.optionSetvos.reduce((optionSetMap, optionSet) => {
        optionSetMap[optionSet.mid] = optionSet

        if (optionSet.selectedVariant) {
          optionSetMap[optionSet.mid].min = optionSet.selectedVariant.min
          optionSetMap[optionSet.mid].max = optionSet.selectedVariant.max
          optionSetMap[optionSet.mid].options = optionSet.selectedVariant.options
        }
        return optionSetMap
      }, {})

      commit('setEntityCol', {
        name: 'optionSets',
        entity: optionSets
      })

      const catalogItemSort = (catalog) => {
        let sortCat = Object.values(catalog)
        const sortFn = (itemArr) => {
          itemArr.sort((itemA, itemB) => {
            if (!itemA.displayOrder || !itemB.displayOrder) return 0
            if (itemA.displayOrder > itemB.displayOrder) return 1
            if (itemA.displayOrder < itemB.displayOrder) return -1
            return 0
          })
          return itemArr
        }

        sortCat.forEach(catObj => {
          if (catObj && !Util.isEmpty(catObj.items)) {
            catObj.items = sortFn(catObj.items)
          }
          if (catObj && !Util.isEmpty(catObj.subCategories)) {
            catObj.subCategories = catalogItemSort(catObj.subCategories)
          }
        })

        sortCat.sort((a, b) => {
          if (!a.displayOrder && !b.displayOrder) return 0
          if (!a.displayOrder) return 1
          if (!b.displayOrder) return -1
          if (a.displayOrder > b.displayOrder) return 1
          if (a.displayOrder < b.displayOrder) return -1
          return 0
        })

        return Util.arrayToMap(sortCat, 'mid')
      }

      const catalog = catalogData.items.reduce((catalog, item) => {
        if (!getters.isItemAvailable(item)) {
          return catalog
        }
        if (item.info.subCategoryId) {
          if (catalog[item.info.categoryId]) {
            if (catalog[item.info.categoryId].subCategories) {
              if (catalog[item.info.categoryId].subCategories[item.info.subCategoryId] && catalog[item.info.categoryId].subCategories[item.info.subCategoryId].items) {
                catalog[item.info.categoryId].subCategories[item.info.subCategoryId].items.push(getters.prepareItemVO(item))
              } else {
                catalog[item.info.categoryId].subCategories[item.info.subCategoryId] = {
                  name: state.categories[item.info.categoryId].subCategories[item.info.subCategoryId].name,
                  mid: item.info.subCategoryId,
                  displayOrder: state.categories[item.info.categoryId].subCategories[item.info.subCategoryId].displayOrder || 999,
                  items: [getters.prepareItemVO(item)]
                }
              }
            } else {
              catalog[item.info.categoryId].subCategories = {
                [item.info.subCategoryId]: {
                  name: state.categories[item.info.categoryId].subCategories[item.info.subCategoryId].name,
                  mid: item.info.subCategoryId,
                  displayOrder: state.categories[item.info.categoryId].subCategories[item.info.subCategoryId].displayOrder || 999,
                  items: [getters.prepareItemVO(item)]
                }
              }
            }
          } else {
            catalog[item.info.categoryId] = {
              name: state.categories[item.info.categoryId].name,
              mid: item.info.categoryId,
              displayOrder: state.categories[item.info.categoryId].displayOrder,
              subCategories: {
                [item.info.subCategoryId]: {
                  name: state.categories[item.info.categoryId].subCategories[item.info.subCategoryId].name,
                  mid: item.info.subCategoryId,
                  displayOrder: state.categories[item.info.categoryId].subCategories[item.info.subCategoryId].displayOrder || 999,
                  items: [getters.prepareItemVO(item)]
                }
              }
            }
          }
        } else if (item.info.categoryId) {
          if (catalog[item.info.categoryId]) {
            if (catalog[item.info.categoryId].items) {
              catalog[item.info.categoryId].items.push(getters.prepareItemVO(item))
            } else {
              catalog[item.info.categoryId].items = [getters.prepareItemVO(item)]
            }
          } else {
            catalog[item.info.categoryId] = {
              name: state.categories[item.info.categoryId].name,
              mid: item.info.categoryId,
              displayOrder: state.categories[item.info.categoryId].displayOrder,
              items: [getters.prepareItemVO(item)]
            }
          }
        }
        return catalog
      }, {
        'rec': {
          mid: 'rec',
          name: 'Recommended',
          displayOrder: -1,
          items: getters.getRecommended
        }
      })

      commit('setEntityCol', {
        name: 'catalog',
        entity: catalogItemSort(catalog)
      })
    }

    catalogApi.getCatalog(params).then(updateCatalog).then(() => {
      commit('setEntityCol', {
        name: 'dataLoaded',
        entity: true
      })
    })
  },

  async getSoldOut ({ commit, state, rootState }) {
    const params = {
      outletId: rootState.business.outletId
    }
    await catalogApi.getSoldOut(params).then(soldOut => {
      commit('setEntityCol', {
        name: 'soldOut',
        data: soldOut,
        id: 'itemId'
      })
    })
  },

  filterCats ({ commit, getters }, catCopy) {
    let temp = []
    let catlogCats = []
    if (Util.isEmpty(catCopy)) {
      return temp
    }
    catlogCats = Util.removeArrFalsyVal(Object.values(catCopy).map(cat => {
      if (cat && (!Util.isEmpty(cat.items) || (!Util.isEmpty(cat.subCategories) && (!Util.isEmpty(Util.pluck(Object.values(cat.subCategories), 'items')))))) {
        return cat
      }
    }))
    catlogCats.forEach(cat => {
      const catName = cat.name
      const catId = cat.mid
      let subCats = []

      if (cat) {
        if (!cat.subCategories) {
          temp.push(cat)
        } else {
          subCats = Object.values(cat.subCategories)
          subCats = subCats.map((subCat) => {
            if (subCat.items) {
              return subCat
            }
          })
          temp.push({
            name: catName,
            mid: catId,
            subCategories: Util.arrayToMap(subCats, 'mid')
          })
        }
      } else if (catId === 'rec') {
        cat = getters.getEntityById('catalog', catId)
        temp.push({
          name: catName,
          mid: catId
        })
      }
    })
    commit('setEntityCol', {
      name: 'filterCats',
      entity: temp
    })
  },

  catalogDataLoaded ({ commit, getters }, dataLoaded) {
    commit('setEntityCol', {
      name: 'dataLoaded',
      entity: dataLoaded
    })
  },

  resetState({ commit }) {
    commit('resetState')
  }
}

const mutations = {
  setEntityCol (state, options) {
    let entity = options.entity

    if (options.data) {
      entity = Util.arrayToMap(options.data, options.id)
    }
    state[options.name] = entity
  },

  resetState(state) {
    Object.assign(state, defaultState())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
